import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, MapPin, Clock, ChevronRight, Star, UtensilsCrossed, Coffee, ShoppingBag, Facebook, Instagram, Twitter, Award } from 'lucide-react';

const Template5 = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    {
      category: 'appetizers',
      name: 'Bruschetta Classic',
      description: 'Toasted bread topped with fresh tomatoes, basil, and garlic',
      price: '$12',
      image: 'https://images.unsplash.com/photo-1572695157366-5e585ab2b69f?w=400&h=300&fit=crop',
      popular: true
    },
    {
      category: 'appetizers',
      name: 'Caesar Salad',
      description: 'Crisp romaine lettuce with parmesan and house-made dressing',
      price: '$10',
      image: 'https://images.unsplash.com/photo-1546793665-c74683f339c1?w=400&h=300&fit=crop',
      popular: false
    },
    {
      category: 'mains',
      name: 'Grilled Salmon',
      description: 'Fresh Atlantic salmon with roasted vegetables and lemon butter',
      price: '$28',
      image: 'https://images.unsplash.com/photo-1467003909585-2f8a72700288?w=400&h=300&fit=crop',
      popular: true
    },
    {
      category: 'mains',
      name: 'Ribeye Steak',
      description: 'Premium 12oz ribeye with garlic mashed potatoes',
      price: '$35',
      image: 'https://images.unsplash.com/photo-1558030006-450675393462?w=400&h=300&fit=crop',
      popular: true
    },
    {
      category: 'mains',
      name: 'Pasta Carbonara',
      description: 'Creamy pasta with bacon, eggs, and parmesan cheese',
      price: '$22',
      image: 'https://images.unsplash.com/photo-1612874742237-6526221588e3?w=400&h=300&fit=crop',
      popular: false
    },
    {
      category: 'desserts',
      name: 'Tiramisu',
      description: 'Classic Italian dessert with coffee and mascarpone',
      price: '$9',
      image: 'https://images.unsplash.com/photo-1571877227200-a0d98ea607e9?w=400&h=300&fit=crop',
      popular: true
    },
    {
      category: 'beverages',
      name: 'Espresso',
      description: 'Rich and bold Italian espresso',
      price: '$4',
      image: 'https://images.unsplash.com/photo-1510591509098-f4fdc6d0ff04?w=400&h=300&fit=crop',
      popular: false
    },
    {
      category: 'beverages',
      name: 'Fresh Lemonade',
      description: 'House-made lemonade with mint',
      price: '$5',
      image: 'https://images.unsplash.com/photo-1523677011781-c91d1bbe2f8f?w=400&h=300&fit=crop',
      popular: false
    },
  ];

  const features = [
    { icon: UtensilsCrossed, title: 'Fine Dining', description: 'Exquisite cuisine crafted by expert chefs' },
    { icon: Coffee, title: 'Premium Coffee', description: 'Artisan coffee made from the finest beans' },
    { icon: Award, title: 'Award Winning', description: 'Recognized for culinary excellence' },
    { icon: Clock, title: 'Fast Service', description: 'Quick and efficient table service' },
  ];

  const gallery = [
    'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1424847651672-bf20a4b0982b?w=500&h=500&fit=crop',
    'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=500&h=500&fit=crop',
  ];

  const testimonials = [
    { name: 'Michael Chen', text: 'Absolutely amazing food and atmosphere! Best dining experience in the city.', rating: 5 },
    { name: 'Sarah Martinez', text: 'The pasta was incredible and the service was impeccable. Highly recommend!', rating: 5 },
    { name: 'David Johnson', text: 'A hidden gem! Great ambiance, delicious food, and friendly staff.', rating: 5 },
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  const filteredMenu = selectedCategory === 'all' 
    ? menuItems 
    : menuItems.filter(item => item.category === selectedCategory);

  const styles = {
    container: {
      minHeight: '100vh',
      background: '#ffffff',
      color: '#1f2937',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    },
    header: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      transition: 'all 0.3s ease',
      background: scrolled ? 'rgba(15, 15, 15, 0.95)' : 'rgba(15, 15, 15, 0.9)',
      backdropFilter: 'blur(20px)',
      boxShadow: '0 2px 20px rgba(0, 0, 0, 0.3)',
    },
    nav: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '1.25rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    logo: {
      fontSize: '1.75rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    navLinks: {
      display: 'flex',
      gap: '2rem',
      alignItems: 'center',
    },
    navButton: {
      background: 'none',
      border: 'none',
      color: '#ffffff',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      fontSize: '1rem',
      fontWeight: '500',
      fontFamily: 'inherit',
    },
    reserveButton: {
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      color: '#1f2937',
      border: 'none',
      padding: '0.75rem 1.75rem',
      borderRadius: '50px',
      cursor: 'pointer',
      fontWeight: '700',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    menuToggle: {
      display: 'none',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: '#ffffff',
    },
    heroSection: {
      position: 'relative',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
      background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.6)), url(https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1600&h=900&fit=crop)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      textAlign: 'center',
      color: 'white',
      padding: '2rem',
    },
    heroContent: {
      maxWidth: '900px',
      zIndex: 10,
    },
    heroTitle: {
      fontSize: 'clamp(3rem, 8vw, 6rem)',
      fontWeight: '800',
      marginBottom: '1.5rem',
      textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
      lineHeight: 1.1,
    },
    heroSubtitle: {
      fontSize: '1.5rem',
      marginBottom: '2.5rem',
      opacity: 0.95,
      lineHeight: 1.6,
    },
    ctaButton: {
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      color: '#1f2937',
      border: 'none',
      padding: '1.25rem 3rem',
      borderRadius: '50px',
      cursor: 'pointer',
      fontSize: '1.2rem',
      fontWeight: '700',
      transition: 'all 0.3s ease',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.75rem',
      boxShadow: '0 10px 30px rgba(251, 191, 36, 0.4)',
    },
    section: {
      padding: '5rem 2rem',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    sectionTitle: {
      fontSize: 'clamp(2rem, 4vw, 3rem)',
      fontWeight: '800',
      color: '#1f2937',
      marginBottom: '1rem',
      textAlign: 'center',
    },
    sectionSubtitle: {
      fontSize: '1.15rem',
      color: '#6b7280',
      marginBottom: '3rem',
      textAlign: 'center',
      maxWidth: '600px',
      margin: '0 auto 3rem',
    },
    featuresGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '2rem',
    },
    featureCard: {
      background: 'white',
      padding: '2.5rem',
      borderRadius: '20px',
      textAlign: 'center',
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
      border: '2px solid transparent',
    },
    featureIcon: {
      width: '80px',
      height: '80px',
      background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
      color: '#d97706',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    featureTitle: {
      fontSize: '1.35rem',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '0.75rem',
    },
    featureDesc: {
      color: '#6b7280',
      lineHeight: 1.6,
    },
    menuSection: {
      background: '#f9fafb',
    },
    filterBar: {
      display: 'flex',
      justifyContent: 'center',
      gap: '1rem',
      marginBottom: '3rem',
      flexWrap: 'wrap',
    },
    filterButton: (isActive) => ({
      background: isActive ? 'linear-gradient(135deg, #fbbf24, #f59e0b)' : 'white',
      color: isActive ? '#1f2937' : '#6b7280',
      border: isActive ? 'none' : '2px solid #e5e7eb',
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      cursor: 'pointer',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      fontSize: '0.95rem',
      textTransform: 'capitalize',
    }),
    menuGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '2rem',
    },
    menuCard: {
      background: 'white',
      borderRadius: '20px',
      overflow: 'hidden',
      boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.4s ease',
      cursor: 'pointer',
      position: 'relative',
    },
    popularBadge: {
      position: 'absolute',
      top: '20px',
      right: '20px',
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      color: '#1f2937',
      padding: '0.5rem 1rem',
      borderRadius: '20px',
      fontSize: '0.875rem',
      fontWeight: '700',
      zIndex: 10,
      boxShadow: '0 4px 15px rgba(251, 191, 36, 0.4)',
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
    },
    menuImage: {
      width: '100%',
      height: '250px',
      objectFit: 'cover',
      transition: 'transform 0.4s ease',
    },
    menuContent: {
      padding: '1.75rem',
    },
    menuHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'start',
      marginBottom: '0.75rem',
    },
    menuName: {
      fontSize: '1.35rem',
      fontWeight: '700',
      color: '#1f2937',
      flex: 1,
    },
    menuPrice: {
      fontSize: '1.5rem',
      fontWeight: '800',
      color: '#f59e0b',
      marginLeft: '1rem',
    },
    menuDesc: {
      color: '#6b7280',
      lineHeight: 1.6,
      marginBottom: '1rem',
    },
    orderButton: {
      width: '100%',
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      color: '#1f2937',
      border: 'none',
      padding: '0.875rem',
      borderRadius: '12px',
      cursor: 'pointer',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
    },
    galleryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '1.5rem',
    },
    galleryItem: {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '20px',
      aspectRatio: '1',
      cursor: 'pointer',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.1)',
    },
    galleryImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.5s ease',
    },
    galleryOverlay: {
      position: 'absolute',
      inset: 0,
      background: 'linear-gradient(to top, rgba(251, 191, 36, 0.9), transparent)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    testimonialsSection: {
      background: '#1f2937',
      color: 'white',
    },
    testimonialsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '2rem',
    },
    testimonialCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      padding: '2.5rem',
      borderRadius: '20px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    testimonialText: {
      fontSize: '1.05rem',
      lineHeight: 1.7,
      marginBottom: '1.5rem',
      fontStyle: 'italic',
      opacity: 0.95,
    },
    testimonialAuthor: {
      fontWeight: '700',
      fontSize: '1.1rem',
      color: '#fbbf24',
    },
    rating: {
      display: 'flex',
      gap: '0.25rem',
      color: '#fbbf24',
      marginTop: '0.5rem',
    },
    contactSection: {
      background: '#f9fafb',
    },
    contactGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '2rem',
    },
    contactCard: {
      background: 'white',
      padding: '2.5rem',
      borderRadius: '20px',
      textAlign: 'center',
      boxShadow: '0 8px 25px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease',
    },
    contactIcon: {
      width: '70px',
      height: '70px',
      background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
      color: '#1f2937',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    contactTitle: {
      fontWeight: '700',
      color: '#1f2937',
      fontSize: '1.2rem',
      marginBottom: '0.75rem',
    },
    contactText: {
      color: '#6b7280',
      lineHeight: 1.6,
    },
    footer: {
      background: '#1f2937',
      color: 'white',
      padding: '3rem 2rem 1.5rem',
    },
    footerContent: {
      maxWidth: '1200px',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '2.5rem',
      marginBottom: '2rem',
    },
    footerTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      marginBottom: '1rem',
      color: '#fbbf24',
    },
    footerText: {
      color: '#d1d5db',
      marginBottom: '1rem',
      lineHeight: 1.6,
    },
    footerLink: {
      color: '#d1d5db',
      display: 'block',
      marginBottom: '0.5rem',
      cursor: 'pointer',
      transition: 'color 0.3s ease',
      textDecoration: 'none',
    },
    socialLinks: {
      display: 'flex',
      gap: '1rem',
      marginTop: '1rem',
    },
    socialIcon: {
      width: '45px',
      height: '45px',
      background: 'rgba(251, 191, 36, 0.1)',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      color: '#fbbf24',
    },
    footerBottom: {
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      paddingTop: '1.5rem',
      textAlign: 'center',
      color: '#9ca3af',
    },
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .nav-button:hover { color: #fbbf24; }
          .reserve-button:hover { transform: scale(1.05); box-shadow: 0 15px 40px rgba(251, 191, 36, 0.5); }
          .cta-button:hover { transform: scale(1.05); box-shadow: 0 15px 45px rgba(251, 191, 36, 0.6); }
          .feature-card:hover { transform: translateY(-10px); box-shadow: 0 15px 50px rgba(251, 191, 36, 0.15); border-color: #fbbf24; }
          .menu-card:hover { transform: translateY(-10px); box-shadow: 0 15px 50px rgba(251, 191, 36, 0.2); }
          .menu-card:hover .menu-image { transform: scale(1.1); }
          .order-button:hover { transform: scale(1.03); }
          .gallery-item:hover .gallery-img { transform: scale(1.15); }
          .gallery-item:hover .gallery-overlay { opacity: 1; }
          .contact-card:hover { transform: translateY(-8px); box-shadow: 0 15px 40px rgba(251, 191, 36, 0.15); }
          .footer-link:hover { color: #fbbf24; }
          .social-icon:hover { background: rgba(251, 191, 36, 0.2); transform: translateY(-3px); }
          @media (max-width: 768px) {
            .menu-toggle { display: block !important; }
            .nav-links { display: none !important; }
          }
        `}
      </style>

      {/* Header */}
      <header style={styles.header}>
        <nav style={styles.nav}>
          <div style={styles.logo}>
            <UtensilsCrossed size={28} />
            SAVORY BITES
          </div>
          <div style={styles.navLinks} className="nav-links">
            <button onClick={() => scrollToSection('home')} style={styles.navButton} className="nav-button">Home</button>
            <button onClick={() => scrollToSection('menu')} style={styles.navButton} className="nav-button">Menu</button>
            <button onClick={() => scrollToSection('gallery')} style={styles.navButton} className="nav-button">Gallery</button>
            <button onClick={() => scrollToSection('reviews')} style={styles.navButton} className="nav-button">Reviews</button>
            <button onClick={() => scrollToSection('contact')} style={styles.navButton} className="nav-button">Contact</button>
            <button style={styles.reserveButton} className="reserve-button">
              <ShoppingBag size={18} />
              Reserve Table
            </button>
          </div>
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} style={styles.menuToggle} className="menu-toggle">
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" style={styles.heroSection}>
        <div style={styles.heroContent}>
          <h1 style={styles.heroTitle}>Experience Culinary Excellence</h1>
          <p style={styles.heroSubtitle}>
            Where passion meets flavor. Indulge in our chef's signature dishes crafted with the finest ingredients.
          </p>
          <button style={styles.ctaButton} className="cta-button" onClick={() => scrollToSection('menu')}>
            Explore Our Menu
            <ChevronRight size={24} />
          </button>
        </div>
      </section>

      {/* Features Section */}
      <section style={styles.section}>
        <h2 style={styles.sectionTitle}>Why Choose Us</h2>
        <p style={styles.sectionSubtitle}>
          Discover what makes our restaurant a favorite destination for food lovers
        </p>
        <div style={styles.featuresGrid}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} style={styles.featureCard} className="feature-card">
                <div style={styles.featureIcon}>
                  <Icon size={36} />
                </div>
                <h3 style={styles.featureTitle}>{feature.title}</h3>
                <p style={styles.featureDesc}>{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" style={{...styles.section, ...styles.menuSection}}>
        <h2 style={styles.sectionTitle}>Our Delicious Menu</h2>
        <p style={styles.sectionSubtitle}>
          Explore our carefully curated selection of mouth-watering dishes
        </p>
        
        <div style={styles.filterBar}>
          <button 
            style={styles.filterButton(selectedCategory === 'all')}
            onClick={() => setSelectedCategory('all')}
          >
            All Items
          </button>
          <button 
            style={styles.filterButton(selectedCategory === 'appetizers')}
            onClick={() => setSelectedCategory('appetizers')}
          >
            Appetizers
          </button>
          <button 
            style={styles.filterButton(selectedCategory === 'mains')}
            onClick={() => setSelectedCategory('mains')}
          >
            Main Course
          </button>
          <button 
            style={styles.filterButton(selectedCategory === 'desserts')}
            onClick={() => setSelectedCategory('desserts')}
          >
            Desserts
          </button>
          <button 
            style={styles.filterButton(selectedCategory === 'beverages')}
            onClick={() => setSelectedCategory('beverages')}
          >
            Beverages
          </button>
        </div>

        <div style={styles.menuGrid}>
          {filteredMenu.map((item, index) => (
            <div key={index} style={styles.menuCard} className="menu-card">
              {item.popular && (
                <div style={styles.popularBadge}>
                  <Star size={14} fill="#1f2937" />
                  Popular
                </div>
              )}
              <img src={item.image} alt={item.name} style={styles.menuImage} className="menu-image" />
              <div style={styles.menuContent}>
                <div style={styles.menuHeader}>
                  <h3 style={styles.menuName}>{item.name}</h3>
                  <div style={styles.menuPrice}>{item.price}</div>
                </div>
                <p style={styles.menuDesc}>{item.description}</p>
                <button style={styles.orderButton} className="order-button">
                  <ShoppingBag size={18} />
                  Add to Order
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" style={styles.section}>
        <h2 style={styles.sectionTitle}>Our Ambiance</h2>
        <p style={styles.sectionSubtitle}>
          Experience the perfect blend of comfort and elegance in our beautiful space
        </p>
        <div style={styles.galleryGrid}>
          {gallery.map((img, index) => (
            <div key={index} style={styles.galleryItem} className="gallery-item">
              <img src={img} alt={`Gallery ${index + 1}`} style={styles.galleryImg} className="gallery-img" />
              <div style={styles.galleryOverlay} className="gallery-overlay"></div>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="reviews" style={{...styles.section, ...styles.testimonialsSection}}>
        <h2 style={{...styles.sectionTitle, color: 'white'}}>What Our Guests Say</h2>
        <p style={{...styles.sectionSubtitle, color: '#d1d5db'}}>
          Read reviews from our delighted customers
        </p>
        <div style={styles.testimonialsGrid}>
          {testimonials.map((testimonial, index) => (
            <div key={index} style={styles.testimonialCard}>
              <p style={styles.testimonialText}>"{testimonial.text}"</p>
              <div style={styles.testimonialAuthor}>{testimonial.name}</div>
              <div style={styles.rating}>
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={18} fill="#fbbf24" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" style={{...styles.section, ...styles.contactSection}}>
        <h2 style={styles.sectionTitle}>Get In Touch</h2>
        <p style={styles.sectionSubtitle}>
          Visit us or reach out for reservations and inquiries
        </p>
        <div style={styles.contactGrid}>
          <div style={styles.contactCard} className="contact-card">
            <div style={styles.contactIcon}>
              <Phone size={32} />
            </div>
            <h3 style={styles.contactTitle}>Phone</h3>
            <p style={styles.contactText}>+1 (555) 123-4567</p>
            <p style={styles.contactText}>+1 (555) 987-6543</p>
          </div>
          <div style={styles.contactCard} className="contact-card">
            <div style={styles.contactIcon}>
              <Mail size={32} />
            </div>
            <h3 style={styles.contactTitle}>Email</h3>
            <p style={styles.contactText}>info@savorybites.com</p>
            <p style={styles.contactText}>reservations@savorybites.com</p>
          </div>
          <div style={styles.contactCard} className="contact-card">
            <div style={styles.contactIcon}>
              <MapPin size={32} />
            </div>
            <h3 style={styles.contactTitle}>Location</h3>
            <p style={styles.contactText}>123 Culinary Street</p>
            <p style={styles.contactText}>Downtown, NY 10001</p>
          </div>
          <div style={styles.contactCard} className="contact-card">
            <div style={styles.contactIcon}>
              <Clock size={32} />
            </div>
            <h3 style={styles.contactTitle}>Hours</h3>
            <p style={styles.contactText}>Mon-Thu: 11:00 AM - 10:00 PM</p>
            <p style={styles.contactText}>Fri-Sun: 11:00 AM - 11:00 PM</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={styles.footer}>
        <div style={styles.footerContent}>
          <div>
            <h4 style={styles.footerTitle}>About Us</h4>
            <p style={styles.footerText}>
              Savory Bites is dedicated to bringing you exceptional dining experiences with fresh, locally-sourced ingredients and innovative culinary techniques.
            </p>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Quick Links</h4>
            <a onClick={() => scrollToSection('menu')} style={styles.footerLink} className="footer-link">Menu</a>
            <a onClick={() => scrollToSection('gallery')} style={styles.footerLink} className="footer-link">Gallery</a>
            <a onClick={() => scrollToSection('reviews')} style={styles.footerLink} className="footer-link">Reviews</a>
            <a onClick={() => scrollToSection('contact')} style={styles.footerLink} className="footer-link">Contact</a>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Services</h4>
            <p style={styles.footerLink} className="footer-link">Dine In</p>
            <p style={styles.footerLink} className="footer-link">Take Out</p>
            <p style={styles.footerLink} className="footer-link">Catering</p>
            <p style={styles.footerLink} className="footer-link">Private Events</p>
          </div>
          <div>
            <h4 style={styles.footerTitle}>Follow Us</h4>
            <p style={styles.footerText}>Stay connected on social media</p>
            <div style={styles.socialLinks}>
              <div style={styles.socialIcon} className="social-icon">
                <Facebook size={22} />
              </div>
              <div style={styles.socialIcon} className="social-icon">
                <Instagram size={22} />
              </div>
              <div style={styles.socialIcon} className="social-icon">
                <Twitter size={22} />
              </div>
            </div>
          </div>
        </div>
        <div style={styles.footerBottom}>
          <p>&copy; 2025 Savory Bites. All rights reserved. Crafted with passion and flavor.</p>
        </div>
      </footer>
    </div>
  );
};

export default Template5;